import { useState, useRef, useEffect, ReactNode } from 'react';
import ToolTipPortalComp from '../tooltip-portal-comp/tooltip-portal-comp';
import ModalPortalComp from '../modal-portal-comp/modal-portal-comp';
import {
  createClickHandler,
  createMouseEnterHandler,
  mapElementsToIndex,
  removeListenersOnElements,
  setListenersOnElements,
} from './content-warning.helpers';
import identity from 'lodash/identity';
import ConfirmModal from '../../modal/confirm-modal/confirm-modal';
import styles from './content-warning.module.scss';

export const WarningTypes = Object.freeze({
  Tooltip: 'Tooltip',
  Modal: 'Modal',
});

type WarningType = keyof typeof WarningTypes;

type ContentWarningProps<ElementType extends HTMLElement = HTMLLinkElement> = {
  message: string;
  children: ReactNode;
  warningType?: WarningType;
  childSelector?: string;
  childFilter?: (arg1: Array<ElementType>) => Array<ElementType>;
  modalOkText: string;
  modalCancelText: string;
};

export const ContentWarning = <
  ElementType extends HTMLElement = HTMLLinkElement,
>({
  message,
  children,
  warningType = WarningTypes.Tooltip,
  childSelector = 'a',
  childFilter = identity,
  modalCancelText,
  modalOkText,
}: ContentWarningProps<ElementType>) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [modalTriggeringElement, setModalTriggeringElement] =
    useState<ElementType | null>(null);
  const [elements, setElements] = useState<ReadonlyArray<ElementType> | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const showModal = warningType === WarningTypes.Modal;

    const domEls = wrapperRef.current.querySelectorAll(childSelector);

    const allTags = Array.from(domEls) as ElementType[];

    const filteredTags = childFilter(allTags);
    setElements(filteredTags);

    const elMap = mapElementsToIndex<ElementType>(filteredTags);

    const handleTagMouseEnter = createMouseEnterHandler<ElementType>(
      elMap,
      setOpenIndex,
    );

    const handleTagMouseLeave = () => {
      setOpenIndex(null);
    };

    const handleLinkClick = createClickHandler<ElementType>(
      showModal,
      modalTriggeringElement,
      setModalTriggeringElement,
      setModalOpen,
      setOpenIndex,
    );

    setListenersOnElements<ElementType>(
      filteredTags,
      showModal,
      handleTagMouseEnter,
      handleTagMouseLeave,
      handleLinkClick,
    );

    return () =>
      removeListenersOnElements<ElementType>(
        filteredTags,
        showModal,
        handleTagMouseEnter,
        handleTagMouseLeave,
        handleLinkClick,
      );
  }, [warningType, modalTriggeringElement, childSelector, childFilter]);

  const handleModalOkClick = () => {
    setModalOpen(false);
    if (!modalTriggeringElement) {
      return;
    }
    modalTriggeringElement.click();
    setModalTriggeringElement(null);
  };

  const handleModalCancelClick = () => {
    setModalOpen(false);
    setModalTriggeringElement(null);
  };

  return (
    <div style={{ height: '100%', flex: 1 }} ref={wrapperRef}>
      {children}
      {elements &&
        elements.map((el, ix) => (
          <ToolTipPortalComp
            key={el.textContent}
            positionToContainer='top'
            toolTipAlignment='center'
            arrowPosition='center'
            isOpen={openIndex === ix}
            text={message}
            parentNode={el}
          />
        ))}
      {warningType === WarningTypes.Modal && (
        <ModalPortalComp
          onRequestClose={handleModalCancelClick}
          shouldCloseOnOuterClick
          shouldCloseOnEsc
          centerOnMobile
          fitContentOnMobile
          removeMaxWidth
          isFullScreen
          transparent
          isOpen={modalOpen}
          showCloseButton={false}
        >
          <div className={styles.modal}>
            <ConfirmModal
              message={message}
              onPrimaryClick={handleModalOkClick}
              primaryLabel={modalOkText}
              secondaryLabel={modalCancelText}
              onSecondaryClick={handleModalCancelClick}
            />
          </div>
        </ModalPortalComp>
      )}
    </div>
  );
};
